import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Center, Loader, Stack, Text, Group, ActionIcon, Tooltip,
  Checkbox, Textarea, Button, ScrollArea, Box, Divider,
} from '@mantine/core';
import { IconRefresh, IconSend } from '@tabler/icons-react';
import ChatBubble from '@/components/advanced/communication/ChatBubble';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useCareSessionService,
  useUserService,
  CareSessionNoteView,
  UpdateCareSessionNoteRequest,
} from '@actschurch/ccms-sdk';

interface SessionNotesPanelProps {
  sessionID: number;
  active: boolean;
  currentUserId?: string;
}

interface NoteItem {
  id: string;
  noteID: number;
  authorId: string;
  authorName: string;
  authorAvatar: string | null;
  entryName: string | null;
  message: string;
  timestamp: string;
  hideFromLeader: boolean;
  hideFromMembers: boolean;
}

function sdkDateToISO(value: number | string): string {
  const raw = String(value ?? '');
  if (raw.length < 14) return new Date().toISOString();
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}`;
}

function decodeNote(value: string): string {
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch {
    return value ?? '';
  }
}

function encodeNote(value: string): string {
  try {
    return btoa(unescape(encodeURIComponent(value)));
  } catch {
    return value;
  }
}

export default function SessionNotesPanel({ sessionID, active, currentUserId }: SessionNotesPanelProps) {
  const Sessions = useCareSessionService();
  const userService = useUserService();

  const [notes, setNotes] = useState<NoteItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const [draft, setDraft] = useState('');
  const [hideFromLeader, setHideFromLeader] = useState(false);
  const [hideFromMembers, setHideFromMembers] = useState(true); // default true per sample payload
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const nameCache = useRef<Map<string, string>>(new Map());

  const resolveName = useCallback(async (userId: string): Promise<string> => {
    if (!userId) return 'Staff';
    if (nameCache.current.has(userId)) return nameCache.current.get(userId)!;
    try {
      const res: any = await userService.getById(userId);
      const user = res?.data ?? res;
      if (user) {
        const name =
          [user.firstName, user.lastName ?? user.surname].filter(Boolean).join(' ').trim() ||
          user.displayName?.trim() ||
          user.email ||
          userId;
        nameCache.current.set(userId, name);
        return name;
      }
    } catch {
      // fall through
    }
    nameCache.current.set(userId, userId);
    return userId;
  }, [userService]);

  const noteToItem = useCallback(async (note: CareSessionNoteView): Promise<NoteItem> => {
  const authorId = note.userId ?? '';
  const authorName =
    note.authorName?.trim() ||
    (authorId ? await resolveName(authorId) : 'Unknown');
  return {
    id: String(note.noteID),
    noteID: note.noteID,
    authorId,
    authorName,
    authorAvatar: note.authorAvatar ?? null,
    entryName: note.entryName ?? null,
    message: decodeNote(note.note ?? ''),
    timestamp: sdkDateToISO(note.entryDateTime),
    hideFromLeader: note.hideFromLeader,
    hideFromMembers: note.hideFromMembers,
  };
}, [resolveName]);

  const fetchNotes = useCallback(async () => {
    if (!sessionID) return;
    setLoading(true);
    try {
      const res: any = await Sessions.notes(sessionID);
      const raw: CareSessionNoteView[] = res?.data ?? res ?? [];
      const sorted = [...raw].sort((a, b) => a.entryDateTime - b.entryDateTime);
      const resolved = await Promise.all(sorted.map(noteToItem));
      setNotes(resolved);
    } catch (e) {
      console.error('Error fetching session notes:', e);
      toast.error('Error', 'Could not load notes.');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [Sessions, sessionID, noteToItem]);

  useEffect(() => { if (active && notes === null) fetchNotes(); }, [active, notes, fetchNotes]);

  const handlePost = async () => {
    const content = draft.trim();
    if (!content) return;
    setPosting(true);
    try {
      await Sessions.addNote(sessionID, {
        sessionID,
        note: encodeNote(content),
        hideFromLeader,
        hideFromMembers,
      } as any);
      setDraft('');
      setHideFromLeader(false);
      setHideFromMembers(true);
      await fetchNotes();
      toast.success('Note Added', 'Your note has been saved.');
    } catch (e) {
      console.error('Error adding session note:', e);
      toast.error('Error', 'Could not save note. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (item: NoteItem) => {
    setEditingId(item.id);
    setEditDraft(item.message);
  };

  const handleSaveEdit = async (item: NoteItem) => {
    const content = editDraft.trim();
    if (!content) return;
    try {
      const data: UpdateCareSessionNoteRequest = { note: encodeNote(content) } as any;
      await Sessions.updateNote(sessionID, item.noteID, data);
      setEditingId(null);
      setEditDraft('');
      await fetchNotes();
      toast.success('Note Updated', 'Your note has been updated.');
    } catch (e) {
      console.error('Error updating session note:', e);
      toast.error('Error', 'Could not update note. Please try again.');
    }
  };

  const handleDelete = async (item: NoteItem) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      const ok = await Sessions.deleteNote(sessionID, item.noteID);
      if (ok) {
        toast.warning('Note Deleted', 'The note has been deleted.');
        await fetchNotes();
      } else {
        toast.error('Error', 'Could not delete note.');
      }
    } catch (e) {
      console.error('Error deleting session note:', e);
      toast.error('Error', 'Could not delete note. Please try again.');
    }
  };

  if (notes === null || (loading && notes.length === 0)) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <>
      <Divider
        label={<Text size="sm" fw={600}>Notes</Text>}
        labelPosition="center"
        mb="md"
      />

      <Group justify="right" mb="xs">
        <Tooltip label="Refresh notes" withArrow>
          <ActionIcon variant="subtle" onClick={fetchNotes} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Stack gap="md">
        <ScrollArea.Autosize mah={360} type="auto">
          <Stack gap={4} py="xs">
            {notes.length === 0 && (
              <Center py="lg">
                <Text size="sm" c="dimmed">No notes yet.</Text>
              </Center>
            )}
            {notes.map((item) => {
              const isOwn = !!currentUserId && item.authorId === currentUserId;

              if (editingId === item.id) {
                return (
                  <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.currentTarget.value)}
                      autosize
                      minRows={2}
                    />
                    <Group gap="xs" justify="flex-end" mt={4}>
                      <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button size="xs" onClick={() => handleSaveEdit(item)}>
                        Save
                      </Button>
                    </Group>
                  </Box>
                );
              }

              const displayMessage = item.entryName ? `${item.entryName}\n\n${item.message}` : item.message;

              return (
                <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                  <Text size="xs" fw={600} ta={isOwn ? 'right' : 'left'} mb={2} px={4}>
                    {item.authorName}
                    {(item.hideFromLeader || item.hideFromMembers) && (
                      <Text component="span" size="xs" c="dimmed" fw={400} ml={6}>
                        {[
                          item.hideFromLeader && 'hidden from leader',
                          item.hideFromMembers && 'hidden from members',
                        ].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                  </Text>
                  <ChatBubble
                    message={displayMessage}
                    sender={{ name: item.authorName, avatar: item.authorAvatar ?? undefined }}
                    timestamp={item.timestamp}
                    isOwn
                    showAvatar={false}
                    onEdit={isOwn ? () => startEdit(item) : undefined}
                    onDelete={isOwn ? () => handleDelete(item) : undefined}
                    onCopy={() => {
                        navigator.clipboard.writeText(item.message);
                        toast.success('Copied', 'Note copied to clipboard.');
                    }}
                    />
                </Box>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>

        <Stack gap="xs">
          <Textarea
            placeholder="Write a note…"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={6}
          />
          <Group justify="space-between">
            <Group gap="md">
              <Checkbox
                label="Hide from leader"
                checked={hideFromLeader}
                onChange={(e) => setHideFromLeader(e.currentTarget.checked)}
              />
              <Checkbox
                label="Hide from members"
                checked={hideFromMembers}
                onChange={(e) => setHideFromMembers(e.currentTarget.checked)}
              />
            </Group>
            <Button
              leftSection={<IconSend size={14} />}
              onClick={handlePost}
              loading={posting}
              disabled={!draft.trim()}
            >
              Post
            </Button>
          </Group>
        </Stack>
      </Stack>
    </>
  );
}